
import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { type ParallelGatewayNodeData } from '@/types/workflow-graph';
import { cn } from '@/lib/utils';

interface ParallelJoinNodeProps {
    data: ParallelGatewayNodeData;
    selected?: boolean;
}

/**
 * Parallel Join Node - Represents the synchronization point for parallel branches.
 * 
 * CRITICAL SEMANTICS:
 * - This is the ONLY valid way to synchronize parallel branches
 * - All branches MUST terminate into this join before proceeding
 * - Cannot be substituted by generic fan-in merge
 * - Execution waits for ALL branches to complete before continuing
 */
export const ParallelJoinNode = memo(function ParallelJoinNode({
    data,
    selected,
}: ParallelJoinNodeProps) {
    const { branchCount = 2, layoutDirection = 'LR' } = data;
    
    // Determine if we're in horizontal (LR) or vertical (TB) flow
    const isHorizontal = layoutDirection === 'LR';
    
    // Bar size based on branches
    const barSize = Math.max(80, branchCount * 40);
    
    // Container dimensions based on orientation
    const containerStyle = isHorizontal 
        ? { width: 20, height: barSize }
        : { width: barSize, height: 20 };
    
    // Bar styling based on orientation
    const barClassName = isHorizontal
        ? 'absolute inset-x-0 mx-auto w-2 rounded-full'
        : 'absolute inset-y-0 my-auto h-2 rounded-full';
    
    const barStyle = isHorizontal
        ? { height: barSize, left: '50%', transform: 'translateX(-50%)' }
        : { width: barSize, top: '50%', transform: 'translateY(-50%)' };
    
    // Input handle position
    const inputPosition = isHorizontal ? Position.Left : Position.Top;
    
    // Output handle position
    const outputPosition = isHorizontal ? Position.Right : Position.Bottom;
    const outputStyle = isHorizontal 
        ? { top: '50%', transform: 'translateY(-50%)' }
        : { left: '50%', transform: 'translateX(-50%)' };
    
    // SYNC indicator position
    const syncStyle = isHorizontal
        ? 'absolute -right-8 top-1/2 -translate-y-1/2'
        : 'absolute -bottom-6 left-1/2 -translate-x-1/2';
    
    // Label position
    const labelStyle = isHorizontal
        ? 'absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap'
        : 'absolute top-1/2 -right-8 -translate-y-1/2 whitespace-nowrap';

    return (
        <div className="relative" style={containerStyle}>
            {/* Input Handles - one for each branch */}
            {Array.from({ length: branchCount }).map((_, idx) => {
                // Match FanOutNode: reverse slot order so join inputs align with branch ends
                // after dagre layout (same branch index / handle id as fork outputs).
                const spacing = barSize / (branchCount + 1);
                const position = spacing * (branchCount - idx);
                
                const handleStyle = isHorizontal
                    ? { top: position, transform: 'translateY(-50%)' }
                    : { left: position, transform: 'translateX(-50%)' };

                return (
                    <Handle
                        key={`branch-${idx}`}
                        type="target"
                        position={inputPosition}
                        id={`branch-${idx}`}
                        className="!h-3 !w-3 !border-gray-900 !bg-gray-800"
                        style={handleStyle}
                    />
                );
            })}

            {/* Solid bar (synchronization point) */}
            <div
                className={cn(
                    barClassName,
                    'bg-gradient-to-b from-gray-900 to-gray-700',
                    'shadow-md',
                    selected && 'ring-2 ring-blue-500 ring-offset-2'
                )}
                style={barStyle}
            />

            {/* SYNC indicator */}
            <div className={syncStyle}>
                <span className="text-[9px] font-bold text-gray-900 bg-gray-200 px-1 rounded">
                    SYNC
                </span>
            </div>

            {/* Output Handle */}
            <Handle
                type="source"
                id="source"
                position={outputPosition}
                className="!h-3 !w-3 !border-gray-900 !bg-gray-800"
                style={outputStyle}
            />

            {/* Label */}
            <div className={labelStyle}>
                <span className="text-[10px] font-medium text-gray-900">
                    Join
                </span>
            </div>
        </div>
    );
});

